import * as ss from 'superstruct';
import { fromEnum } from '../utils/from-enum';
import { NonBlankString } from './non-blank-string';

/** Backend route for the active org's brand asset library. */
export const BrandAssetApiPath = '/api/brand-assets';

/** The multipart field an upload's file is sent in. */
export const BrandAssetUploadField = 'file';

export enum BrandAssetKind {
  Image = 'image',
  Font = 'font',
}

/** Largest file that can be uploaded. */
export const BrandAssetMaxBytes = 5 * 1024 * 1024;

export const BrandAssetNameMaxLength = 200;

/** The file types an asset can be, by MIME type. */
export const BrandAssetMimeTypes: Readonly<Record<string, BrandAssetKind>> = {
  'image/png': BrandAssetKind.Image,
  'image/jpeg': BrandAssetKind.Image,
  'image/gif': BrandAssetKind.Image,
  'image/webp': BrandAssetKind.Image,
  'image/svg+xml': BrandAssetKind.Image,
  'font/woff2': BrandAssetKind.Font,
  'font/woff': BrandAssetKind.Font,
  'font/ttf': BrandAssetKind.Font,
  'font/otf': BrandAssetKind.Font,
};

/**
 * For a file input's `accept`. Browsers often have no MIME type for fonts, so
 * their extensions are listed too.
 */
export const brandAssetAccept = (kind?: BrandAssetKind): string => {
  const mimeTypes = Object.keys(BrandAssetMimeTypes).filter(
    (mimeType) => !kind || BrandAssetMimeTypes[mimeType] === kind,
  );
  const fontExtensions =
    kind === BrandAssetKind.Image ? [] : ['.woff2', '.woff', '.ttf', '.otf'];
  return [...mimeTypes, ...fontExtensions].join(',');
};

/** An asset's id, as image fields and font values store it. */
export const BrandAssetId = ss.pattern(
  ss.string(),
  /^[0-9a-f]{8}-(?:[0-9a-f]{4}-){3}[0-9a-f]{12}$/,
);

export const BrandAssetDto = ss.type({
  id: BrandAssetId,
  kind: fromEnum(BrandAssetKind),
  name: ss.string(),
  mimeType: ss.string(),
  byteSize: ss.integer(),
  /** Images only. */
  width: ss.optional(ss.integer()),
  height: ss.optional(ss.integer()),
  /** Hidden from the library's pickers; versions using it are unaffected. */
  archived: ss.boolean(),
  createdAt: ss.string(),
  /** A short-lived link to the original file. */
  url: ss.string(),
  /** A short-lived link to a downscaled WebP copy. Images only. */
  previewUrl: ss.optional(ss.string()),
});
export type BrandAssetDto = ss.Infer<typeof BrandAssetDto>;

export const BrandAssetUsage = ss.type({
  /** True when the draft uses the asset. */
  draft: ss.boolean(),
  /** Published version numbers using the asset, ascending. */
  versions: ss.array(ss.integer()),
});
export type BrandAssetUsage = ss.Infer<typeof BrandAssetUsage>;

/** An asset as the library lists it. Only unused assets can be deleted. */
export const BrandAssetLibraryItem = ss.assign(
  BrandAssetDto,
  ss.type({ usage: BrandAssetUsage }),
);
export type BrandAssetLibraryItem = ss.Infer<typeof BrandAssetLibraryItem>;

export const BrandAssetLibrary = ss.array(BrandAssetLibraryItem);

const assetName = NonBlankString(BrandAssetNameMaxLength);

/** The text fields sent with an upload; `name` defaults to the file's name. */
export const BrandAssetUploadRequest = ss.object({
  name: ss.optional(assetName),
});
export type BrandAssetUploadRequest = ss.Infer<typeof BrandAssetUploadRequest>;

export const BrandAssetUpdateRequest = ss.object({
  name: ss.optional(assetName),
  archived: ss.optional(ss.boolean()),
});
export type BrandAssetUpdateRequest = ss.Infer<typeof BrandAssetUpdateRequest>;
